import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PageLoading } from '@/components/ui/loading';
import { FormError } from '@/components/ui/form-error';
import { ROLES } from '@/lib/permissions';
import type { BlockReportPhoto, Project } from '@/lib/types';
import {
  ApprovalActualSheet,
  ApprovalRequirementSheet,
  buildActualRows,
  buildRequirementRows,
  type ApprovalActualRow,
  type ApprovalRequirementRow,
  type ProjectBlock,
} from './PendingApprovalReports';
import '../reports/print-report.css';
import './pending-approval-report.css';

type ApprovalsTab = 'requirements' | 'actuals';

type ReqSummary = {
  id: string;
  requirementNumber: string | null;
  projectName: string | null;
  workDate: string | null;
  status: string;
  submittedBy: string | null;
  blockId: string | null;
  blockName: string | null;
  requiredSkilled: number;
  requiredUnskilled: number;
  typeOfWork: string | null;
  locationOfWork: string | null;
  descriptionOfWork: string | null;
  grandTotal: number;
};

type ActSummary = {
  id: string;
  actualNumber: string | null;
  projectName: string | null;
  workDate: string | null;
  status: string;
  submittedBy: string | null;
  blockId: string | null;
  blockName: string | null;
  actualSkilled: number;
  actualUnskilled: number;
  typeOfWork: string | null;
  locationOfWork: string | null;
  descriptionOfWork: string | null;
  actualWorkDone: string | null;
  remarks: string | null;
  photos: BlockReportPhoto[];
  totalCost: number;
};

type DecisionResult = { id: string; status: string; reason?: string; toStatus?: string };

const REMARK_REQUIRED_MESSAGE = 'Remarks / Report is required.';

export default function PendingApprovalsPage() {
  const { hasRole } = useAuth();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ApprovalsTab>('requirements');
  const [projectFilter, setProjectFilter] = useState('');
  const [selectedReq, setSelectedReq] = useState<string[]>([]);
  const [selectedAct, setSelectedAct] = useState<string[]>([]);
  const [remark, setRemark] = useState('');
  const [remarkError, setRemarkError] = useState('');
  const remarkRef = useRef<HTMLTextAreaElement>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const pageProjectId = projectFilter === '' ? undefined : projectFilter;

  // Admins (tenant org admin + platform SUPER_ADMIN) keep full org-wide access
  // and are never pinned to an assigned project.
  const isFullAccess = hasRole(ROLES.ORG_ADMIN) || hasRole(ROLES.SUPER_ADMIN);

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['approvals-projects'],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: Project[] }>('/projects');
      return (res.data ?? []) as Project[];
    },
  });

  // For scoped users the /projects endpoint already returns only assigned
  // projects. When exactly one is assigned, select it so the approvals open
  // directly instead of asking for a project the user has no choice over.
  const autoSelectRef = useRef(true);
  useEffect(() => {
    if (!autoSelectRef.current) return;
    if (projects === undefined) return;
    autoSelectRef.current = false;
    if (isFullAccess) return;
    if (projects.length === 1) setProjectFilter(projects[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, isFullAccess]);

  const { data: requirements, isLoading: reqLoading } = useQuery({
    queryKey: ['pending-approvals', pageProjectId],
    queryFn: async () => {
      const url = `/requirements/pending/approvals?projectId=${pageProjectId}`;
      const res = await api.get<{ success: boolean; data: any[] }>(url);
      return (res.data ?? []) as any[];
    },
    enabled: !!pageProjectId,
  });

  const { data: actuals, isLoading: actLoading } = useQuery({
    queryKey: ['pending-approvals-actuals', pageProjectId],
    queryFn: async () => {
      const url = `/actual-status/pending/approvals?projectId=${pageProjectId}`;
      const res = await api.get<{ success: boolean; data: any[] }>(url);
      return (res.data ?? []) as any[];
    },
    enabled: !!pageProjectId,
  });

  const { data: projectBlocks } = useQuery({
    queryKey: ['approval-blocks', pageProjectId],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: any[] }>(`/blocks/all?projectId=${pageProjectId}`);
      return (res.data ?? []) as ProjectBlock[];
    },
    enabled: !!pageProjectId,
  });

  const isActiveReq = activeTab === 'requirements';
  const activeIds = isActiveReq ? selectedReq : selectedAct;
  const selectedCount = activeIds.length;
  const activeList = isActiveReq ? requirements || [] : actuals || [];

  const pendingIds = useMemo(
    () => activeList.map((r: any) => r.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab, requirements, actuals],
  );

  const {
    data: summaryData,
    isLoading: summaryLoading,
    isError: summaryError,
    refetch: refetchSummary,
  } = useQuery<{ items: ReqSummary[] | ActSummary[]; grandTotal: number } | null>({
    queryKey: ['approval-summary', activeTab, pageProjectId, pendingIds.join('|')],
    queryFn: async () => {
      if (pendingIds.length === 0) return null;
      if (isActiveReq) {
        const res = await api.post<{ success: boolean; data: { items: ReqSummary[]; grandTotal: number } }>('/requirements/approvals/summary', { ids: pendingIds });
        return res.data;
      }
      const res = await api.post<{ success: boolean; data: { items: ActSummary[]; grandTotal: number } }>('/actual-status/approvals/summary', { ids: pendingIds });
      return res.data;
    },
    enabled: !!pageProjectId && pendingIds.length > 0,
  });

  const rows = useMemo(() => {
    const items = (summaryData?.items ?? []) as any;
    return (isActiveReq ? buildRequirementRows(items, projectBlocks) : buildActualRows(items, projectBlocks)) as
      | ApprovalRequirementRow[]
      | ApprovalActualRow[];
  }, [summaryData, isActiveReq, projectBlocks]);

  const allRecordIds = useMemo(
    () => Array.from(new Set(pendingIds)),
    [pendingIds],
  );
  const allSelected = allRecordIds.length > 0 && activeIds.length === allRecordIds.length;
  const partial = activeIds.length > 0 && !allSelected;
  const selectedSet = useMemo(() => new Set(activeIds), [activeIds]);

  function toggleIds(recordIds: string[]) {
    if (recordIds.length === 0) return;
    const set = new Set(activeIds);
    const allOn = recordIds.every((id) => set.has(id));
    if (allOn) {
      recordIds.forEach((id) => set.delete(id));
    } else {
      recordIds.forEach((id) => set.add(id));
    }
    const next = Array.from(set);
    if (isActiveReq) setSelectedReq(next);
    else setSelectedAct(next);
  }

  function toggleAll() {
    const next = allSelected ? [] : allRecordIds;
    if (isActiveReq) setSelectedReq(next);
    else setSelectedAct(next);
  }

  function clearSelection() {
    if (isActiveReq) setSelectedReq([]);
    else setSelectedAct([]);
  }

  function afterDecision() {
    queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
    queryClient.invalidateQueries({ queryKey: ['pending-approvals-actuals'] });
    queryClient.invalidateQueries({ queryKey: ['approval-workload'] });
    queryClient.invalidateQueries({ queryKey: ['approval-summary'] });
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }

  function requireRemark(): boolean {
    if (remark.trim() === '') {
      setRemarkError(REMARK_REQUIRED_MESSAGE);
      remarkRef.current?.focus();
      return false;
    }
    setRemarkError('');
    return true;
  }

  function handleApproveClick() {
    if (selectedCount === 0) return;
    if (!requireRemark()) return;
    setApproveOpen(true);
  }

  function handleRejectClick() {
    if (selectedCount === 0) return;
    if (!requireRemark()) return;
    setRejectOpen(true);
  }

  const approveMutation = useMutation({
    mutationFn: async () => {
      const payload = { ids: activeIds, remarks: remark.trim() };
      if (isActiveReq) {
        const res = await api.post<{ success: boolean; data: { results: DecisionResult[]; approvedCount: number } }>('/requirements/approvals/approve', payload);
        return res.data;
      }
      const res = await api.post<{ success: boolean; data: { results: DecisionResult[]; approvedCount: number } }>('/actual-status/approvals/approve', payload);
      return res.data;
    },
    onSuccess: (data) => {
      setApproveOpen(false);
      addToast(`${data.approvedCount} record${data.approvedCount === 1 ? '' : 's'} approved`, 'success');
      clearSelection();
      setRemark('');
      setRemarkError('');
      afterDecision();
    },
    onError: (err) => {
      addToast((err as Error).message || 'Unable to approve the selected records.', 'error');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      const payload = { ids: activeIds, remarks: remark.trim() };
      if (isActiveReq) {
        const res = await api.post<{ success: boolean; data: { results: DecisionResult[]; rejectedCount: number } }>('/requirements/approvals/reject', payload);
        return res.data;
      }
      const res = await api.post<{ success: boolean; data: { results: DecisionResult[]; rejectedCount: number } }>('/actual-status/approvals/reject', payload);
      return res.data;
    },
    onSuccess: (data) => {
      setRejectOpen(false);
      addToast(`${data.rejectedCount} record${data.rejectedCount === 1 ? '' : 's'} rejected`, 'success');
      clearSelection();
      setRemark('');
      setRemarkError('');
      afterDecision();
    },
    onError: (err) => {
      addToast((err as Error).message || 'Unable to reject the selected records.', 'error');
    },
  });

  if (reqLoading || actLoading) return <PageLoading />;

  // Scoped users with no assignments cannot select a project, so surface a
  // clear state instead of a permanently empty requester.
  if (!projectsLoading && !isFullAccess && (projects ?? []).length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary">Pending Approvals</h1>
          <p className="mt-1 text-sm text-secondary">
            {hasRole(ROLES.CLUSTER_HEAD)
              ? 'Final-review the reports below before final approval.'
              : 'Review the reports below before approval.'}
          </p>
        </div>
        <Card>
          <CardContent className="p-10 text-center">
            <p className="text-sm text-secondary">No projects assigned. Contact your administrator to get project access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isCluster = hasRole(ROLES.CLUSTER_HEAD);
  const stageLabel = isCluster ? 'Final Review Stage' : 'Approval Stage';
  const stageRole = isCluster ? 'Cluster Head' : 'Project Manager';
  const projectName = (projects ?? []).find((p) => p.id === pageProjectId)?.name ?? '';

  const reportLoading = summaryLoading || (pendingIds.length > 0 && !summaryData && !summaryError);
  const selectedLabels = (summaryData?.items ?? [])
    .filter((it: any) => activeIds.includes(it.id))
    .map((it: any) => (isActiveReq ? it.requirementNumber : it.actualNumber))
    .filter(Boolean);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-primary">Pending Approvals</h1>
        <p className="mt-1 text-sm text-secondary">
          {isCluster
            ? 'Final-review the reports below before final approval. Tick the block rows to select the corresponding pending records, enter your remarks, and confirm.'
            : 'Review the reports below before approval. Tick the block rows to select the corresponding pending records, enter your remarks, and confirm.'}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="approval-project">Project *</Label>
          <Select value={projectFilter} onValueChange={(v) => { setProjectFilter(v); clearSelection(); setRemark(''); setRemarkError(''); }}>
            <SelectTrigger id="approval-project" className="w-full sm:w-[240px]"><SelectValue placeholder="Select Project" /></SelectTrigger>
            <SelectContent>
              {(projects ?? []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!pageProjectId ? (
        <Card>
          <CardContent className="p-10 text-center">
            <p className="text-sm text-secondary">Select a project to view pending approvals.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="rounded-lg border bg-surface px-4 py-3">
            <p className="text-sm font-bold uppercase tracking-wide text-primary">{stageLabel}</p>
            <p className="text-sm text-secondary">{stageRole}</p>
          </div>

          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as ApprovalsTab); clearSelection(); setRemark(''); setRemarkError(''); }}>
            <TabsList>
              <TabsTrigger value="requirements">Departmental Labour-Requirement</TabsTrigger>
              <TabsTrigger value="actuals">Departmental Labour -Actual</TabsTrigger>
            </TabsList>
          </Tabs>

          <Card>
            <CardContent className="p-4">
              {reportLoading ? (
                <p className="py-10 text-center text-sm text-muted-foreground">Loading report...</p>
              ) : summaryError ? (
                <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
                  <p className="text-sm text-red-500">Unable to load the report. Please try again.</p>
                  <Button variant="outline" size="sm" onClick={() => refetchSummary()}>Retry</Button>
                </div>
              ) : pendingIds.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  No pending {isActiveReq ? 'Departmental Labour-Requirement' : 'Departmental Labour -Actual'} for the selected project.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  {isActiveReq ? (
                    <ApprovalRequirementSheet
                      projectName={projectName}
                      rows={rows as ApprovalRequirementRow[]}
                      selectedIds={selectedSet}
                      onToggleRecordIds={toggleIds}
                      allSelected={allSelected}
                      partial={partial}
                      onToggleAll={toggleAll}
                    />
                  ) : (
                    <ApprovalActualSheet
                      projectName={projectName}
                      rows={rows as ApprovalActualRow[]}
                      selectedIds={selectedSet}
                      onToggleRecordIds={toggleIds}
                      allSelected={allSelected}
                      partial={partial}
                      onToggleAll={toggleAll}
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-3 rounded-lg border bg-surface p-4">
            <div>
              <Label htmlFor="approval-remark">Remarks / Report *</Label>
              <Textarea
                id="approval-remark"
                ref={remarkRef}
                value={remark}
                onChange={(e) => {
                  setRemark(e.target.value);
                  if (remarkError && e.target.value.trim() !== '') setRemarkError('');
                }}
                placeholder="Enter your remarks / report..."
                rows={3}
                aria-invalid={remarkError ? true : undefined}
                aria-describedby={remarkError ? 'approval-remark-error' : undefined}
                className={remarkError ? 'border-error' : undefined}
              />
              <FormError id="approval-remark-error" message={remarkError} />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm" className="bg-success text-white hover:opacity-90" disabled={selectedCount === 0} onClick={handleApproveClick}>
                Approve Selected ({selectedCount})
              </Button>
              <Button size="sm" variant="destructive" disabled={selectedCount === 0} onClick={handleRejectClick}>
                Reject Selected ({selectedCount})
              </Button>
            </div>
          </div>

          {/* Approval confirmation */}
          <AlertDialog open={approveOpen} onOpenChange={setApproveOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Approve {selectedCount} Selected Record{selectedCount === 1 ? '' : 's'}?</AlertDialogTitle>
                <AlertDialogDescription>
                  {isCluster
                    ? 'This is the final approval for the selected record(s).'
                    : 'This approves the selected record(s) at the Manager stage and sends them to the Cluster Head for final review.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium text-primary">Remarks / Report: </span>
                  <span className="text-secondary">{remark.trim() || '—'}</span>
                </div>
                <div>
                  <span className="font-medium text-primary">Records:</span>
                  <ul className="mt-1 list-inside list-disc space-y-0.5 text-secondary">
                    {selectedLabels.length > 0 ? selectedLabels.map((n) => <li key={n}>{n}</li>) : <li>Selected records</li>}
                  </ul>
                </div>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={approveMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-success text-white hover:opacity-90"
                  disabled={approveMutation.isPending}
                  onClick={(e) => { e.preventDefault(); approveMutation.mutate(); }}
                >
                  {approveMutation.isPending ? 'Approving...' : `Approve Selected (${selectedCount})`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Rejection confirmation */}
          <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reject {selectedCount} Selected Record{selectedCount === 1 ? '' : 's'}?</AlertDialogTitle>
                <AlertDialogDescription>
                  The selected record(s) will be sent back to the submitter for revision.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium text-primary">Remarks / Report: </span>
                  <span className="text-secondary">{remark.trim() || '—'}</span>
                </div>
                <div>
                  <span className="font-medium text-primary">Records:</span>
                  <ul className="mt-1 list-inside list-disc space-y-0.5 text-secondary">
                    {selectedLabels.length > 0 ? selectedLabels.map((n) => <li key={n}>{n}</li>) : <li>Selected records</li>}
                  </ul>
                </div>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={rejectMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-error text-white hover:opacity-90"
                  disabled={rejectMutation.isPending}
                  onClick={(e) => { e.preventDefault(); rejectMutation.mutate(); }}
                >
                  {rejectMutation.isPending ? 'Rejecting...' : `Reject Selected (${selectedCount})`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}